import React, { useState, useRef, useEffect } from 'react';
import type { DatePickerProps } from '../../../types/dateTypes';
import { dateUtils } from '../../../utils/dateUtils';
import Calendar from './Calendar';
import './DatePicker.css';

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder = 'MM/DD/YYYY',
  label,
  disabled = false,
  validationRules,
  className = '',
  error,
  name,
  position = 'above'
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    value ? dateUtils.parseDate(value) || undefined : undefined
  );
  const [isMobile, setIsMobile] = useState(dateUtils.isMobile());
  const calendarPosition = position; // Use the position prop (default: 'above' for single trip)
  const inputRef = useRef<HTMLInputElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Update internal state when value prop changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
      setSelectedDate(value ? dateUtils.parseDate(value) || undefined : undefined);
    }
  }, [value]);

  // Update mobile detection on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(dateUtils.isMobile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle input field changes (for manual typing)
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);

    // Try to parse the date as user types
    const parsedDate = dateUtils.parseDate(newValue);
    if (parsedDate && !dateUtils.isDateDisabled(parsedDate, validationRules)) {
      setSelectedDate(parsedDate);
      onChange(dateUtils.formatDate(parsedDate));
    } else if (!newValue) {
      setSelectedDate(undefined);
      onChange('');
    }
  };


  // Handle input field focus (open calendar on all devices)
  const handleInputFocus = () => {
    if (!disabled) {
      setIsCalendarOpen(true);
    }
  };

  // Handle input field click (open calendar on all devices)
  const handleInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (!disabled) {
      setIsCalendarOpen(true);
    }
  };

  // Handle date selection from calendar
  const handleDateSelect = (date: Date) => {
    const formattedDate = dateUtils.formatDate(date);
    setSelectedDate(date);
    setInputValue(formattedDate);
    onChange(formattedDate);
    setIsCalendarOpen(false);
    
    // Focus back to input after selection
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Handle calendar close
  const handleCalendarClose = () => {
    setIsCalendarOpen(false);
  };

  // Handle keyboard events
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!disabled) {
        setIsCalendarOpen(!isCalendarOpen);
      }
    } else if (event.key === 'Escape') {
      setIsCalendarOpen(false);
    } else if (event.key === 'ArrowDown' && !isCalendarOpen) {
      event.preventDefault();
      if (!disabled) {
        setIsCalendarOpen(true);
      }
    }
  };

  // FIXED: Always use custom calendar for consistent experience across web and mobile
  // The custom calendar is already mobile-optimized with responsive CSS
  // Previously, mobile devices used native <input type="date"> but now all devices use the custom calendar

  // Custom calendar version - now used for both desktop and mobile
  return (
    <div className={`date-picker-container ${isMobile ? 'mobile' : 'desktop'} ${className}`}>
      {label && (
        <label className="date-picker-label" htmlFor={name}>
          {label}
        </label>
      )}
      <div className="date-picker-wrapper">
        <input
          ref={inputRef}
          type="text"
          id={name}
          name={name}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={`date-picker-input custom ${error ? 'error' : ''} ${isCalendarOpen ? 'open' : ''}`}
          aria-describedby={error ? `${name}-error` : undefined}
          aria-expanded={isCalendarOpen}
          aria-haspopup="dialog"
          role="combobox"
          autoComplete="off"
        />
        <div className="date-picker-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
        </div>
        
        {isCalendarOpen && (
          <div
            ref={calendarRef}
            className={`calendar-dropdown ${calendarPosition} ${isMobile ? 'mobile-calendar' : ''}`}
          >
            <Calendar
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              validationRules={validationRules}
              onClose={handleCalendarClose}
              isVisible={isCalendarOpen}
            />
          </div>
        )}
      </div>
      {error && (
        <div id={`${name}-error`} className="date-picker-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default DatePicker;